import * as React from "react";
import { DateFormats, ExportedAppointment } from "intake_shared/src/types";
import moment from "../../../../../utils/app_moment";
import { getBytes, getMetadata, getStorage, ref } from "firebase/storage";
import styles from "./FindExportedAppointments.module.scss";
import { Input } from "../../../bits/Input/Input";
import { Button } from "../../../bits/Button/Button";
import Table from "../../../bits/Table/Table";

type ExportFileMetadata = {
    dateUpdated: string;
    exportSize: number;
};

const APPOINTMENT_EXPORT_FILE_PATH = "appointment_exports/appointments.json";

export default function FindExportedAppointments() {
    const [exportFileMetadata, setExportFileMetadata] =
        React.useState<ExportFileMetadata>({ dateUpdated: "", exportSize: 0 });
    const [clinicianEmailAddress, setClinicianEmailAddress] = React.useState("");
    const [isLoading, setIsLoading] = React.useState(false);
    const [exportedAppointments, setExportedAppointments] = React.useState<
        ExportedAppointment[]
    >([]);

    const fetchMetadata = async (
        filePath = APPOINTMENT_EXPORT_FILE_PATH
    ): Promise<ExportFileMetadata> => {
        const storage = getStorage();
        const appointmentFileRef = ref(storage, filePath);
        try {
            const metadata = await getMetadata(appointmentFileRef);
            return { dateUpdated: metadata.updated, exportSize: metadata.size };
        } catch (ex) {
            console.error(ex);
            return { dateUpdated: "", exportSize: 0 };
        }
    };

    const fetchBytes = async (
        filePath = APPOINTMENT_EXPORT_FILE_PATH
    ): Promise<any> => {
        const storage = getStorage();
        const appointmentFileRef = ref(storage, filePath);
        try {
            const bytesArray = await getBytes(appointmentFileRef);
            return JSON.parse(new TextDecoder().decode(bytesArray));
        } catch (ex) {
            console.error(ex);
            return {};
        }
    };

    React.useEffect(() => {
        (async () => {
            const { dateUpdated, exportSize } = await fetchMetadata();
            if (dateUpdated && exportSize > 0) {
                setExportFileMetadata({ dateUpdated, exportSize });
            }
        })();
    }, []);

    const columns = [
        {
            Header: "Client Email",
            accessor: "client_email",
        },
        {
            Header: "Client name",
            accessor: (row: ExportedAppointment) =>
                `${row.client_first_name} ${row.client_last_name}`,
        },
        {
            Header: "DOB",
            accessor: (row: ExportedAppointment) => row.client_dob.split("T")[0],
        },
        {
            Header: "Appointment",
            accessor: (row: ExportedAppointment) =>
                moment(row.start).format(DateFormats.DAY_TIME_FORMAT),
        },
        {
            Header: "Recurring",
            accessor: "recurrenceRule",
        },
        {
            Header: "Fee",
            accessor: (row: ExportedAppointment) => `$${row.fee}`,
        },
        {
            Header: "Location",
            accessor: "location",
        },
        {
            Header: "Service",
            accessor: "requested_service",
        },
        {
            Header: "Type",
            accessor: (row: ExportedAppointment) =>
                row.appointment_type === "teleconsult" ? "Teleconsult" : "Walk-In",
        },
    ];

    return (
        <div className={styles.exportedApptContainer}>
            <div className={styles.header}>
                <p className={styles.headerTitle}>Exported Appointments</p>
                <div className={styles.headerContent}>
                    <p>
                        Use this page to find appointments that have been exported
                        within the last 24hours.
                    </p>
                    <p>
                        Enter the clinician email and click on load appointment to
                        begin
                    </p>
                    <p>
                        <strong>Last Exported Time:</strong>{" "}
                        {moment(exportFileMetadata.dateUpdated).format(
                            DateFormats.DAY_YEAR_HOUR_MIN_FORMAT
                        )}
                        ,<strong> Size:</strong>{" "}
                        {(exportFileMetadata.exportSize / 1000000).toFixed(2)}
                        mb
                    </p>
                </div>
                <div className={styles.formWrapper}>
                    <Input
                        isRequired
                        type="email"
                        label="Clinician email address"
                        placeholder="Enter client’s email"
                        value={clinicianEmailAddress}
                        onChange={(e) => setClinicianEmailAddress(e.target.value)}
                        inputContainerClassName={styles.inputContainer}
                    />
                    <div className={styles.buttonWrapper}>
                        <Button
                            onClick={async (e) => {
                                setIsLoading(true);
                                try {
                                    const jsonData = await fetchBytes();
                                    const exportedAppts = (jsonData[
                                        clinicianEmailAddress
                                    ] || {})["appointments"];
                                    if (exportedAppts) {
                                        setExportedAppointments(exportedAppts);
                                    }
                                } catch (e) {
                                    console.error(e);
                                } finally {
                                    setIsLoading(false);
                                }
                            }}
                            className={styles.actionButton}
                        >
                            {isLoading ? "Loading..." : "Load Appointments"}
                        </Button>
                        <Button
                            variant="secondary"
                            onClick={() => {
                                setClinicianEmailAddress("");
                                setExportedAppointments([]);
                                setIsLoading(false);
                            }}
                            className={styles.actionButton}
                        >
                            Reset
                        </Button>
                    </div>
                </div>
            </div>
            {exportedAppointments.length ? (
                <div className={styles.tableWrapper}>
                    <p className={styles.apptCount}>
                        Found {exportedAppointments.length} Appointments.
                    </p>
                    <Table
                        columns={columns}
                        data={exportedAppointments}
                        allowSelect={false}
                    />
                </div>
            ) : null}
        </div>
    );
}
